/**
 * Client half of the calculation progress protocol.
 *
 * The wire format is defined by the server in server/calculation/progress.ts — keep the types
 * below in sync with it. (They are duplicated rather than shared because external/sx-common-ts is
 * a git submodule shared with the other sx repos, and this format is local to sx-column.)
 *
 * A calculation route answers in one of two shapes, and the client must accept both:
 *
 *  - Plain JSON, exactly as before, when the call finished quickly.
 *  - NDJSON — zero or more `progress` lines followed by one `result` or `error` line — when the
 *    call ran long enough for the server to start reporting.
 *
 * A progress line's `pct` is interpolated from expected durations, except on a run whose
 * calculation service reported its own counts, where it is computed from them and `step`/`totalSteps` carry
 * them. Nothing on the wire distinguishes the two — the fields are read the same way either way.
 */

/** Mirrors CalcOperation on the server. */
export type CalcOperation = "analyse" | "codecheck" | "design";

/**
 * What makes one progress step distinct from the next.
 *
 * Prefers the server's `stepId`, which is the authoritative identity — it knows the load
 * combination's ordinal, the design candidate and the bending plane, none of which are derivable
 * from the rest of the line. The fallback is for lines with no engine tick behind them, where the
 * phase and the name are all there is.
 *
 * Both places that de-duplicate progress use this. They used to derive their own keys, and each
 * was missing a different dimension, so distinct steps were silently dropped.
 */
export const calcStepKey = (line: { stepId?: string; phase: string; detail?: string }): string =>
  line.stepId ?? `${line.phase}|${line.detail ?? ""}`;

/** Mirrors CalcPhase on the server. */
export type CalcPhase = "saving" | "analysis" | "codecheck" | "design" | "persisting";

export interface CalcProgressLine {
  kind: "progress";
  op: CalcOperation;
  phase: CalcPhase;
  /** i18next key, resolved as `${labelKey}.label` — same convention as CalcError.label. */
  labelKey: string;
  /** 0..90. Never reaches 100: only the result line means the run is done. */
  pct: number;
  elapsedMs: number;
  ts: string;
  /** The run has passed its expected duration — show the "taking longer than usual" note. */
  slow?: true;
  /**
   * The engine's own count of what it is working on ("load combination 7 of 24"), present only
   * for a run that went through a design service that reports its progress. `step` restarts at 1
   * in each pass of a multi-pass run, so it is a readout of the current item, not a fraction of
   * the run — `pct` is the fraction. `totalSteps` is absent when the length is not known ahead.
   */
  step?: number;
  totalSteps?: number;
  /**
   * The name of what the engine is working on, straight from the project — "LC12", "ULS 3".
   * Untranslatable data, so the wording around it is supplied here, not by the server.
   */
  detail?: string;
  /** What this step belongs to — the load combination the engine last named. */
  context?: string;
  /** Set instead of `context` on a line that names what the following steps belong to. */
  contextLabelKey?: string;
  /** Which repetition of the code check this step belongs to; design runs only. */
  designPass?: number;
  /** Which bending direction (plane variant) the step came from — "XZ" or "XY". */
  direction?: string;
  /**
   * Set when the step reads from a label of its own — the engine's activity rather than a thing
   * in the project ("Cracking iteration 3", "Waiting for FEM-Design (12 s)"). Resolved as
   * `${stepLabelKey}.label` with {{step}}, {{total}} and {{name}}.
   */
  stepLabelKey?: string;
  /** Server-computed identity of this step; see CalcProgressLine.stepId on the server. */
  stepId?: string;
}

/**
 * Body of a successful calculation — the same shape the routes have always returned. It never
 * carries calc errors: a calc that logs an error fails the run, so errors only ever arrive on
 * CalcErrorLine (or, for a non-streamed failure, on the plain error body).
 */
export interface CalcResultBody {
  blob?: { data?: unknown };
  log?: unknown;
  suggestions?: unknown[];
}

export interface CalcResultLine extends CalcResultBody {
  kind: "result";
}

export interface CalcErrorLine {
  kind: "error";
  errors?: unknown;
  blob?: unknown;
}

export type CalcStreamLine = CalcProgressLine | CalcResultLine | CalcErrorLine;

/** What a finished read produced: either a result body, or the failure the server reported. */
export type CalcStreamOutcome =
  | { kind: "result"; body: CalcResultBody }
  | { kind: "error"; errors: unknown; blob?: unknown };

/**
 * A line is only treated as part of the stream protocol if it parses on its own AND carries a
 * string `kind`. That second condition is what makes reading tolerant of the plain-JSON shape:
 * Express pretty-prints JSON bodies outside production (`json spaces` defaults to 2 in
 * development), so a plain response arrives as many newline-separated fragments, none of which
 * parse individually. Those fall through and get parsed as one document at the end.
 */
const parseStreamLine = (raw: string): CalcStreamLine | null => {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (parsed && typeof parsed === "object" && typeof (parsed as { kind?: unknown }).kind === "string") {
      return parsed as CalcStreamLine;
    }
  } catch {
    // Not a complete JSON value — part of a pretty-printed plain body.
  }
  return null;
};

/**
 * Read a calculation response, reporting progress lines as they arrive.
 *
 * `onProgress` is called for every progress line; throttling is the caller's business. The
 * returned outcome is the terminating line, or — for a plain JSON response — the whole body.
 */
export async function readCalcStream(
  response: Response,
  onProgress: (line: CalcProgressLine) => void,
): Promise<CalcStreamOutcome> {
  // Older browsers and some test doubles give no readable body; nothing to stream, so fall
  // straight back to the pre-existing behaviour.
  if (!response.body) {
    return { kind: "result", body: (await response.json()) as CalcResultBody };
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let text = "";
  let consumed = 0;
  let outcome: CalcStreamOutcome | null = null;

  // Consume as many complete protocol lines as the buffer holds. Stops at the first line that
  // is not one — either an incomplete tail, or a plain-JSON body that never was a stream.
  const drain = () => {
    for (;;) {
      const nl = text.indexOf("\n", consumed);
      if (nl === -1) return;
      const line = parseStreamLine(text.slice(consumed, nl));
      if (!line) {
        // Nothing consumed yet: this was never a stream, so leave the buffer for the whole-body
        // fallback. Mid-stream, skip the unreadable line rather than freezing consumption and
        // losing a terminating line that is right behind it.
        if (consumed === 0) return;
        consumed = nl + 1;
        continue;
      }
      consumed = nl + 1;
      if (line.kind === "progress") {
        onProgress(line);
      } else if (line.kind === "result") {
        const { kind, ...body } = line;
        outcome = { kind: "result", body };
      } else {
        outcome = { kind: "error", errors: line.errors, blob: line.blob };
      }
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    text += decoder.decode(value, { stream: true });
    drain();
    // The terminating line is the last thing the server writes, but do not rely on the socket
    // closing promptly afterwards — take the answer and let go of the body.
    if (outcome) {
      void reader.cancel();
      return outcome;
    }
  }

  text += decoder.decode();
  drain();
  if (outcome) return outcome;

  const rest = text.slice(consumed).trim();
  if (rest === "") {
    // Progress arrived but the response ended without a result or error line: the server died
    // or the connection dropped mid-calculation.
    throw new Error("The calculation ended without returning a result.");
  }
  return { kind: "result", body: JSON.parse(rest) as CalcResultBody };
}
